import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_volley-club/artifacts/dg49xs0l_Black%20Decorative%20Shield%20Volleyball%20Club%20Logo%20%28Sweatshirts%29%20%28T-Shirts%29.png";

export default function Navbar({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Only show full nav menu if user is logged in
  const navItems = user ? [
    { path: '/home', label: 'Home', icon: '🏠' },
    { path: '/games', label: 'Games', icon: '🏐' },
    { path: '/my-stats', label: 'My Stats', icon: '📊' },
    { path: '/leaderboards', label: 'Leaderboards', icon: '🏆' },
    { path: '/about', label: 'About', icon: 'ℹ️' },
    { path: '/profile', label: 'Profile', icon: '👤' },
  ] : [];

  if (user && user.is_admin) {
    navItems.push({ path: '/admin', label: 'Admin', icon: '👑' });
  }

  const handleNavClick = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  return (
    <>
      <nav className="bg-white/80 backdrop-blur-md shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <div
              onClick={() => navigate('/home')}
              className="flex items-center gap-2 sm:gap-3 cursor-pointer"
            >
              <img src={LOGO_URL} alt="NLV Logo" className="w-10 h-10 sm:w-12 sm:h-12 object-contain" />
              <span className="text-xl sm:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-blue-600">
                NLV
              </span>
            </div>

            {/* Desktop Nav Items */}
            <div className="hidden md:flex items-center gap-2">
              {user && navItems.map((item) => (
                <Button
                  key={item.path}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  onClick={() => navigate(item.path)}
                  variant={location.pathname === item.path ? 'default' : 'ghost'}
                  size="sm"
                  className={`px-4 ${
                    location.pathname === item.path
                      ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white'
                      : 'text-gray-700 hover:text-orange-600'
                  }`}
                >
                  <span className="text-lg">{item.icon}</span>
                  <span className="ml-2">{item.label}</span>
                </Button>
              ))}

              {user && (
                <Button
                  data-testid="settings-btn"
                  onClick={() => navigate('/settings')}
                  variant={location.pathname === '/settings' ? 'default' : 'ghost'}
                  size="sm"
                  className={`px-4 ${
                    location.pathname === '/settings'
                      ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white'
                      : 'text-gray-700 hover:text-orange-600'
                  }`}
                >
                  <span className="text-lg">⚙️</span>
                  <span className="ml-2">Settings</span>
                </Button>
              )}

              {user && (
                <Button
                  data-testid="logout-btn"
                  onClick={onLogout}
                  variant="outline"
                  size="sm"
                  className="ml-4 border-red-500 text-red-600 hover:bg-red-50"
                >
                  Logout
                </Button>
              )}
            </div>

            {/* Mobile Hamburger Button - Only show if user is logged in */}
            {user && (
              <button
                className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                <svg 
                  className="w-6 h-6 text-gray-700" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  {mobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay - Only show if user is logged in */}
      {user && mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setMobileMenuOpen(false)}>
          <div 
            className="absolute right-0 top-0 h-full w-64 bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-8">
                <h2 className="text-xl font-bold text-gray-900">Menu</h2>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 rounded-lg hover:bg-gray-100"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-2">
                {navItems.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => handleNavClick(item.path)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                      location.pathname === item.path
                        ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <span className="text-xl">{item.icon}</span>
                    <span className="font-medium">{item.label}</span>
                  </button>
                ))}

                <button
                  onClick={() => handleNavClick('/settings')}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                    location.pathname === '/settings'
                      ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <span className="text-xl">⚙️</span>
                  <span className="font-medium">Settings</span>
                </button>

                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    onLogout();
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left text-red-600 hover:bg-red-50 transition-colors mt-4"
                >
                  <span className="text-xl">🚪</span>
                  <span className="font-medium">Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
